import React from 'react';
import { useSelector } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import TrustBadges from '../../components/marketing/TrustBadges';
import FaqAccordion from '../../components/marketing/FaqAccordion';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './Pricing.css';

// LEGAL_REVIEW_REQUIRED: all fee/refund/pricing wording on this page is marketing copy and must be
// reviewed by counsel before production.
// COMPLIANCE: fees are percentage-only (never a flat service-fee option) and the booking/visit fee
// goes to the PLATFORM (the customer still sees and pays it). The numbers shown on this page are
// pulled LIVE from the DB per category (Category.travellingPrice / userServiceFeeValue /
// partnerServiceFeeValue) so the page always follows what admin has configured — no hardcoded fees.

const WHY = [
  { icon: 'M12 1v22m5-18H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6', title: 'Industry-low fees', text: 'Our fees are among the lowest anywhere — more of every dollar stays with you and your pro.' },
  { icon: 'M9 12l2 2 4-4M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6z', title: 'No hidden charges', text: 'What you see at checkout is what you pay. No surprise add-ons after the job.' },
  { icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z', title: 'Pay only when it’s done', text: 'Your card is on file, but you’re charged when the work is complete.' },
  { icon: 'M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', title: 'See the price first', text: 'Get an upfront estimate before you book — no hourly guesswork.' },
];

// Pick the most common (mode) value of a numeric list; ties resolve to the first-seen value.
function mode(values) {
  const counts = new Map();
  let best = null;
  let bestCount = 0;
  for (const v of values) {
    const c = (counts.get(v) || 0) + 1;
    counts.set(v, c);
    if (c > bestCount) {
      bestCount = c;
      best = v;
    }
  }
  return best;
}

// Format a percentage value (drop trailing ".0").
const pct = (v) => `${Number.isInteger(v) ? v : Number(v.toFixed(2))}%`;
// Format a dollar value with no cents when whole (e.g. $10, $1), else 2dp.
const money = (v) => `$${Number.isInteger(v) ? v : Number(v).toFixed(2)}`;

// Derive the live fee figures (and the copy that depends on them) from the loaded categories.
// Guards against empty/SSR-before-load data with sensible "varies by service" fallbacks.
function deriveFees(cats) {
  const visitFees = (cats || []).map((c) => Number(c.travellingPrice)).filter((v) => !isNaN(v));
  const serviceFees = (cats || []).map((c) => Number(c.userServiceFeeValue)).filter((v) => !isNaN(v));
  const workerFees = (cats || []).map((c) => Number(c.partnerServiceFeeValue)).filter((v) => !isNaN(v));

  const hasData = visitFees.length > 0 && serviceFees.length > 0;

  const visitMin = visitFees.length ? Math.min(...visitFees) : null;
  const visitMax = visitFees.length ? Math.max(...visitFees) : null;
  const visitVaries = visitMin !== visitMax;

  const serviceTypical = serviceFees.length ? mode(serviceFees) : null;
  const serviceMin = serviceFees.length ? Math.min(...serviceFees) : null;
  const serviceMax = serviceFees.length ? Math.max(...serviceFees) : null;
  const serviceVaries = serviceMin !== serviceMax;

  const workerTypical = workerFees.length ? mode(workerFees) : null;

  // Display strings.
  // Visit fee: a range when it varies, otherwise the single value.
  const visitDisplay = !hasData
    ? 'varies by service'
    : visitVaries
      ? `${money(visitMin)}–${money(visitMax)}`
      : money(visitMax);

  // Service fee: the typical (mode); note it can vary.
  const serviceDisplay = !hasData ? 'varies by service' : pct(serviceTypical);
  const serviceBadge = !hasData
    ? 'Transparent service fee'
    : serviceVaries
      ? `${pct(serviceTypical)} service fee`
      : `${pct(serviceTypical)} service fee`;

  const workerDisplay = workerTypical != null ? pct(workerTypical) : '10%';

  return {
    hasData,
    visitMin,
    visitMax,
    visitVaries,
    visitDisplay,
    serviceTypical,
    serviceMin,
    serviceMax,
    serviceVaries,
    serviceDisplay,
    serviceBadge,
    workerTypical,
    workerDisplay,
  };
}

// LEGAL_REVIEW_REQUIRED: pricing FAQ answers reviewed before prod. Built from live fee figures.
function buildFaqs(f) {
  const visitPhrase = f.hasData
    ? f.visitVaries
      ? `a per-booking visit fee that depends on the service (currently ${f.visitDisplay})`
      : `a one-time ${f.visitDisplay} booking fee per booking`
    : 'a per-booking visit fee that depends on the service';
  const servicePhrase = f.hasData
    ? f.serviceVaries
      ? `a service fee (typically ${f.serviceDisplay})`
      : `a ${f.serviceDisplay} service fee`
    : 'a transparent service fee';
  return [
    {
      q: 'What fees does BestHand charge?',
      a: `You pay your pro’s rate plus ${servicePhrase} and ${visitPhrase}. The visit fee is always shown upfront before you book — no hidden charges.`,
    },
    {
      q: 'Where does the booking (visit) fee go?',
      a: 'The booking fee goes to the platform to cover matching, support, and secure payments. It depends on the service and you see it clearly before you book.',
    },
    {
      q: 'Are your fees really lower than others?',
      a: 'Yes — our fees are among the lowest anywhere, so more of every dollar stays with you and your pro.',
    },
    {
      q: 'Could my final bill change?',
      a: 'Your estimate can change if the job needs extra materials, more time, or additional workers. Any changes are shown before they’re charged.',
    },
    {
      q: 'Do you have promo codes or credits?',
      a: 'Yes. Credits apply automatically at checkout, and you can see how much you have left on your remaining uses.',
    },
  ];
}

function buildJsonLd(f, faqs) {
  const visitPhrase = f.hasData
    ? f.visitVaries
      ? `a per-booking visit fee (currently ${f.visitDisplay}, varies by service)`
      : `a one-time ${f.visitDisplay} booking fee`
    : 'a per-booking visit fee that varies by service';
  const servicePhrase = f.hasData ? `a ${f.serviceDisplay} service fee` : 'a transparent service fee';
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Offer',
      name: 'BestHand booking',
      description: `Upfront, transparent pricing: your pro’s rate plus ${servicePhrase} and ${visitPhrase}.`,
      priceCurrency: 'USD',
      seller: { '@type': 'Organization', name: 'BestHand' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((q) => ({ '@type': 'Question', name: q.q, acceptedAnswer: { '@type': 'Answer', text: q.a } })),
    },
  ];
}

function Pricing() {
  const cats = useSelector((state) => state.categories?.data) || [];
  const f = deriveFees(cats);
  const faqs = buildFaqs(f);
  const jsonLd = buildJsonLd(f, faqs);

  // Worked example: a $100 job with the typical service fee % and a representative (max) visit fee.
  const exampleService = f.hasData ? f.serviceTypical : 13;
  const exampleVisit = f.hasData ? f.visitMax : 15;
  const exampleServiceAmt = (100 * exampleService) / 100;
  const exampleTotal = 100 + exampleServiceAmt + exampleVisit;

  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      <Hero
        eyebrow="Pricing"
        title="Upfront pricing. No surprises."
        subtitle="See the price before you book. Industry-low fees, clearly itemized — more of every dollar stays with you and your pro."
        primaryLabel="Browse services"
        primaryTo="/category?type=all"
        secondaryLabel="Get the app"
        secondaryTo="/get-app"
        badges={[f.serviceBadge, f.hasData ? `${f.visitDisplay} visit fee` : 'Upfront visit fee', 'No hidden charges']}
      />

      {/* What you pay */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>What you pay</h2>
          <p className={s.lead}>Two simple, transparent fees on top of your pro’s rate — that’s it.</p>
          <div className={s.feeGrid}>
            <div className={s.feeCard}>
              <div className={s.feeBig}>{f.serviceDisplay}</div>
              <h3 className={s.feeTitle}>Service fee</h3>
              {/* COMPLIANCE: percentage-only, never a flat option. */}
              <p className={s.feeText}>
                {f.hasData && f.serviceVaries
                  ? `Typically ${f.serviceDisplay} of your total bill (it can vary by service). Percentage-based and transparent — never a hidden flat charge.`
                  : `${f.serviceDisplay} of your total bill. Percentage-based and transparent — never a hidden flat charge.`}
              </p>
            </div>
            <div className={s.feeCard}>
              <div className={s.feeBig}>{f.hasData ? f.visitDisplay : '—'}</div>
              <h3 className={s.feeTitle}>Visit fee</h3>
              {/* COMPLIANCE: booking/visit fee goes to the PLATFORM; customer still sees & pays it. */}
              <p className={s.feeText}>
                {f.hasData
                  ? `A per-booking visit fee that depends on the service (currently ${f.visitDisplay}) — always shown upfront before you book. It covers matching, support, and secure payments.`
                  : 'A per-booking visit fee that depends on the service, always shown upfront before you book. It covers matching, support, and secure payments.'}
              </p>
            </div>
            <div className={s.feeCard}>
              <div className={s.feeBig}>{f.workerDisplay}</div>
              <h3 className={s.feeTitle}>Pro payout fee</h3>
              <p className={s.feeText}>Pros keep the rest of every job. Our {f.workerDisplay} payout fee is among the lowest in the industry.</p>
            </div>
          </div>

          {/* Worked example */}
          <div className={s.example}>
            <h3 className={s.exampleTitle}>A simple example</h3>
            <ul className={s.exampleRows}>
              <li><span>Job total</span><span>$100.00</span></li>
              <li><span>Service fee ({pct(exampleService)})</span><span>{money(exampleServiceAmt)}</span></li>
              <li><span>Visit fee (varies by service)</span><span>{money(exampleVisit)}</span></li>
              <li className={s.exampleTotal}><span>You pay</span><span>{money(exampleTotal)}</span></li>
            </ul>
            {/* LEGAL_REVIEW_REQUIRED: example figures are illustrative; confirm final rounding/tax handling. */}
            <p className={s.exampleNote}>Illustrative example using a representative service. Your actual total depends on the job, the service, and your pro’s rate.</p>
          </div>
        </div>
      </section>

      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <TrustBadges heading="Why our pricing is different" items={WHY} />
        </div>
      </section>

      {/* How it's calculated */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>How your price is calculated</h2>
          <p className={s.lead}>You get an upfront estimate before you book. Here’s what can affect the final bill.</p>
          <div className={s.calcGrid}>
            <div className={s.calcCard}><h3>Materials</h3><p>If your pro buys parts or supplies for the job, those are added and shown to you.</p></div>
            <div className={s.calcCard}><h3>Extra time</h3><p>If a job runs longer than estimated, the additional time is itemized before it’s charged.</p></div>
            <div className={s.calcCard}><h3>Additional workers</h3><p>Bigger jobs may need more than one pro — you’ll see this reflected in the estimate.</p></div>
            <div className={s.calcCard}><h3>Multi-day work</h3><p>Longer projects are broken down clearly so you always know what you’re paying for.</p></div>
          </div>
        </div>
      </section>

      {/* Cancellation & changes — policy confirmed by Paul */}
      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <div className={s.cancelCard}>
            <h2 className={s.h2}>Cancellations & changes</h2>
            {/* LEGAL_REVIEW_REQUIRED: cancellation terms confirmed by Paul; counsel to review final wording. */}
            <ul className={s.cancelList}>
              <li><strong>Free</strong> to cancel before a pro accepts your booking.</li>
              <li><strong>Free</strong> when you cancel more than <strong>24 hours</strong> before the scheduled time.</li>
              <li>
                Cancel <strong>less than 24 hours</strong> before the job and a <strong>one-hour fee</strong>{' '}
                (one hour at the service rate) applies — so the pro who set the time aside isn’t left empty-handed.
              </li>
            </ul>
            <p className={s.cancelText}>
              Need to change or cancel a booking?{' '}
              <Link to="/contact-us" className={s.inlineLink}>Contact support</Link> and our team will help.
            </p>
          </div>
        </div>
      </section>

      {/* Promo codes & credits */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <div className={s.promoCard}>
            <div>
              <h2 className={s.h2Left}>Promo codes & credits</h2>
              <p className={s.promoText}>
                Have a promo code or referral credit? It applies automatically at checkout, and you’ll see
                how much you have left across your remaining uses (for example, “$75 left · 3/4 uses”).
              </p>
            </div>
            <Link to="/leave-a-review" className={s.promoBtn}>Leave a review</Link>
          </div>
        </div>
      </section>

      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <FaqAccordion heading="Pricing questions" items={faqs} />
        </div>
      </section>

      <CtaBanner
        title="Know the price before you book."
        subtitle="Transparent, industry-low fees on every job."
        primaryLabel="Browse services"
        primaryTo="/category?type=all"
        secondaryLabel="See how it works"
        secondaryTo="/how-it-works"
      />
    </main>
  );
}

export default withStyles(s)(Pricing);
